/**
 * The small status widgets, in one place so every screen paints risk the same
 * way. If a colour means something different on two screens, it means nothing.
 */

import type { Liveness } from '../api/types';
import { DASH } from './format';

/**
 * The liveness pill.
 *
 * These units are legitimately dark for days: the truck they are in has no
 * signal, or the site has no wifi, or nobody has switched them on this week.
 * `offline` is therefore painted a calm blue, not red — an operations tool that
 * cries wolf about a normal condition gets ignored about the abnormal one.
 * `long_offline` (quiet for over a week) is amber. `never_reported` is
 * deliberately colourless with a hollow marker: it is the absence of a reading,
 * not a bad reading.
 */
const LIVENESS_TEXT: Record<Liveness, string> = {
  online: 'Online',
  offline: 'Offline',
  long_offline: 'Long offline',
  never_reported: 'No data ever',
};

const LIVENESS_HELP: Record<Liveness, string> = {
  online: 'Heard from in the last 15 minutes.',
  offline:
    'Quiet for under a week. Normal for these units — they are legitimately dark for days.',
  long_offline: 'Quiet for over a week. Worth chasing.',
  never_reported: 'This unit has never sent anything. Not a gap in the data — a gap in the fleet.',
};

export function LivenessPill({ liveness }: { liveness: Liveness }) {
  return (
    <span className={`pill ${liveness}`} title={LIVENESS_HELP[liveness]}>
      {LIVENESS_TEXT[liveness]}
    </span>
  );
}

/** State of charge, with the bar coloured by how much trouble it implies. */
export function Soc({ value }: { value: number | null }) {
  if (value === null || value === undefined) {
    return <span className="nodata">{DASH}</span>;
  }
  const level = value < 15 ? 'critical' : value < 30 ? 'low' : 'ok';
  return (
    <span className={`meter ${level}`}>
      <span className="bar">
        <i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </span>
      <span className="val">{value.toFixed(1)}%</span>
    </span>
  );
}

/**
 * Coverage: the fraction of a period that was actually sampled.
 *
 * Shown next to every energy figure and never optional. A kWh from these units
 * is integrated over sampled time, so it is honest about what was measured and
 * says nothing at all about the hours that were not. Coverage is the part that
 * says how much of the period the figure covers, and without it "12.4 kWh
 * yesterday" reads as a full day when it might be twenty minutes.
 */
/** Coverage band: `thin` under a quarter sampled, `partial` under 90%, else `full`.
 *  Exported so a tile can wear the same colour as the inline bar — one
 *  definition, so the two can never disagree about what counts as thin. */
export function covLevel(value: number | null | undefined): 'thin' | 'partial' | 'full' {
  if (value === null || value === undefined) return 'thin';
  const p = Math.max(0, Math.min(1, value));
  return p < 0.25 ? 'thin' : p < 0.9 ? 'partial' : 'full';
}

export function Coverage({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <span className="nodata">{DASH}</span>;
  const p = Math.max(0, Math.min(1, value));
  const level = covLevel(value);
  return (
    <span
      className={`cov ${level}`}
      title={`${(p * 100).toFixed(1)}% of this period was actually sampled. The energy figure covers only that fraction.`}
    >
      <span className="bar">
        <i style={{ width: `${p * 100}%` }} />
      </span>
      <span className="val">{(p * 100).toFixed(0)}%</span>
    </span>
  );
}

export function FaultCount({ faults, warnings }: { faults: number; warnings: number }) {
  if (faults === 0 && warnings === 0) {
    return <span className="hint">none</span>;
  }
  return (
    <span className="row" style={{ gap: 4 }}>
      {faults > 0 && (
        <span className="pill bad" title="Open faults">
          {faults} fault{faults === 1 ? '' : 's'}
        </span>
      )}
      {warnings > 0 && (
        <span className="pill neutral" title="Open warnings">
          {warnings} warning{warnings === 1 ? '' : 's'}
        </span>
      )}
    </span>
  );
}

/**
 * The standard rendering for a value this product cannot measure.
 *
 * Battery temperature is the case this exists for: the column is in the schema,
 * the BMS register exists, and the inverter's mirror has no slot for it, so it
 * is always NULL on a Power Droid. An empty chart implies a sensor that stopped;
 * a zero implies a freezing battery. Saying so in words is the only rendering
 * that is not a lie.
 */
export function NotOnThisModel({ what, why }: { what: string; why?: string }) {
  return (
    <div className="unavailable-panel">
      <strong>{what}: not available on this model</strong>
      {why ?? 'The product does not report this value, so there is nothing to plot.'}
    </div>
  );
}
